import { api } from './index'

// --- Shared union types ---

export type PromotionGenerationType = 'single_use' | 'static'
export type PromotionState = 'active' | 'draft' | 'inactive'
export type DiscountType = 'amount' | 'percent' | 'item' | 'fixed_price'

// AASM state of Promotion::Pool's generation_state column.
// See thanx-nucleus app/models/promotion/pool.rb. Backend may add new
// states; UI logic is URL-driven and treats unknown values as "in progress".
export type CodesCsvState =
  | 'pending'
  | 'generating'
  | 'generating_csv'
  | 'complete'

// --- Raw API types (snake_case, internal only) ---

type RawPromotionPool = {
  generation_type: PromotionGenerationType
  static_code: string | null
  total_codes: number
  redeemed_count: number
  active_count: number
  codes_csv_state: CodesCsvState | null
  codes_csv_url: string | null
}

type RawPromotionPayload = {
  id: string
  name: string
  state: PromotionState
  discount_type: DiscountType
  discount_value: number | null
  // Both fields are non-null only when discount_type === 'fixed_price'.
  // discount_scope drives the Detail page rendering ('Free entree' vs
  // 'Fixed price - $5'); price_cap is exposed alongside discount_value
  // (which already returns price_cap for fixed_price) for callers that
  // want the unambiguous field name.
  discount_scope: string | null
  price_cap: number | null
  minimum_spend: number | null
  maximum_discount: number | null
  fine_print: string | null
  instructions: string | null
  redemption_venue: string
  starts_at: string | null
  ends_at: string | null
  pool: RawPromotionPool | null
  // Optional: older merchant-api versions omit this field.
  redeem_template_id?: number | null
  // Day-of-week / time-of-day restrictions. Optional — older merchant-api
  // versions (pre-DATA-5280) omit these.
  redeem_restriction_time_range?: boolean | null
  redeem_restriction_time_range_description?: string | null
  redeem_restriction_time_zone?: string | null
  redeem_restriction_monday?: number[] | null
  redeem_restriction_tuesday?: number[] | null
  redeem_restriction_wednesday?: number[] | null
  redeem_restriction_thursday?: number[] | null
  redeem_restriction_friday?: number[] | null
  redeem_restriction_saturday?: number[] | null
  redeem_restriction_sunday?: number[] | null
  created_at: string
  updated_at: string
}

// --- Normalized camelCase types (used by UI) ---

export type PromotionPool = {
  generationType: PromotionGenerationType
  staticCode: string | null
  totalCodes: number
  redeemedCount: number
  activeCount: number
  codesCsvState: CodesCsvState | null
  codesCsvUrl: string | null
}

export type PromotionPayload = {
  id: string
  name: string
  state: PromotionState
  discountType: DiscountType
  discountValue: number | null
  discountScope: string | null
  priceCap: number | null
  minimumSpend: number | null
  maximumDiscount: number | null
  finePrint: string | null
  instructions: string | null
  redemptionVenue: string
  startsAt: string | null
  endsAt: string | null
  pool: PromotionPool | null
  redeemTemplateId: number | null
  // Day-of-week / time-of-day restrictions, normalised to camelCase.
  // Optional to tolerate older backends that don't yet return them
  // (see DATA-5280). All values are nullable.
  redeemRestrictionTimeRange?: boolean | null
  redeemRestrictionTimeRangeDescription?: string | null
  redeemRestrictionTimeZone?: string | null
  redeemRestrictionMonday?: number[] | null
  redeemRestrictionTuesday?: number[] | null
  redeemRestrictionWednesday?: number[] | null
  redeemRestrictionThursday?: number[] | null
  redeemRestrictionFriday?: number[] | null
  redeemRestrictionSaturday?: number[] | null
  redeemRestrictionSunday?: number[] | null
  createdAt: string
  updatedAt: string
}

// --- Transform helpers ---

function transformPool(raw: RawPromotionPool): PromotionPool {
  return {
    generationType: raw.generation_type,
    staticCode: raw.static_code,
    totalCodes: raw.total_codes,
    redeemedCount: raw.redeemed_count,
    activeCount: raw.active_count,
    codesCsvState: raw.codes_csv_state,
    codesCsvUrl: raw.codes_csv_url,
  }
}

function transformPromotion(raw: RawPromotionPayload): PromotionPayload {
  return {
    id: raw.id,
    name: raw.name,
    state: raw.state,
    discountType: raw.discount_type,
    discountValue: raw.discount_value,
    discountScope: raw.discount_scope ?? null,
    priceCap: raw.price_cap ?? null,
    minimumSpend: raw.minimum_spend,
    maximumDiscount: raw.maximum_discount,
    finePrint: raw.fine_print,
    instructions: raw.instructions,
    redemptionVenue: raw.redemption_venue,
    startsAt: raw.starts_at,
    endsAt: raw.ends_at,
    pool: raw.pool ? transformPool(raw.pool) : null,
    redeemTemplateId: raw.redeem_template_id ?? null,
    redeemRestrictionTimeRange: raw.redeem_restriction_time_range ?? null,
    redeemRestrictionTimeRangeDescription:
      raw.redeem_restriction_time_range_description ?? null,
    redeemRestrictionTimeZone: raw.redeem_restriction_time_zone ?? null,
    redeemRestrictionMonday: raw.redeem_restriction_monday ?? null,
    redeemRestrictionTuesday: raw.redeem_restriction_tuesday ?? null,
    redeemRestrictionWednesday: raw.redeem_restriction_wednesday ?? null,
    redeemRestrictionThursday: raw.redeem_restriction_thursday ?? null,
    redeemRestrictionFriday: raw.redeem_restriction_friday ?? null,
    redeemRestrictionSaturday: raw.redeem_restriction_saturday ?? null,
    redeemRestrictionSunday: raw.redeem_restriction_sunday ?? null,
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
  }
}

// --- Transformed response types ---

type GetPromotionsResponse = { promotions: PromotionPayload[] }

type GetPromotionResponse = { promotion: PromotionPayload }

type GetPromotionsParams = {
  page?: number
  per_page?: number
}

// --- Request param types (snake_case for API) ---

// Day-of-week + time-of-day restriction params. These are forwarded
// to thanx-merchant-api as plain snake_case fields, mirroring the
// existing Campaign Builder `Program::Redeem::Restriction` contract
// (see DATA-5280 for backend wiring).
export type PromotionDayTimeParams = {
  redeem_restriction_time_range?: boolean
  redeem_restriction_time_range_description?: string | null
  redeem_restriction_time_zone?: string
  redeem_restriction_monday?: number[]
  redeem_restriction_tuesday?: number[]
  redeem_restriction_wednesday?: number[]
  redeem_restriction_thursday?: number[]
  redeem_restriction_friday?: number[]
  redeem_restriction_saturday?: number[]
  redeem_restriction_sunday?: number[]
}

export type CreatePromotionParams = {
  name: string
  discount_type: DiscountType
  discount_value: number
  // FixedPrice-only — read-only forwarded from the chosen reward template
  // by the wizard. Backend validates it; merchants do not configure it
  // through the promo wizard (rewards UI is the source of truth).
  discount_scope?: string
  // Id of the reward template the merchant picked. The backend links it on
  // the promo redeem so item-scoped templates keep their product-ID scoping.
  template_id?: number
  state?: PromotionState
  minimum_spend?: number | null
  maximum_discount?: number | null
  fine_print?: string | null
  instructions?: string | null
  redemption_venue?: string
  starts_at?: string | null
  ends_at?: string | null
  static_code?: string
  code_count?: number
} & PromotionDayTimeParams

export type UpdatePromotionParams = {
  id: string
  name?: string
  state?: PromotionState
  discount_type?: DiscountType
  discount_value?: number
  discount_scope?: string
  // See CreatePromotionParams — re-sent on edit so a template switch also
  // switches the inherited product-ID scoping.
  template_id?: number
  minimum_spend?: number | null
  maximum_discount?: number | null
  fine_print?: string | null
  instructions?: string | null
  redemption_venue?: string
  starts_at?: string | null
  ends_at?: string | null
  static_code?: string
} & PromotionDayTimeParams

// --- Promotion Code types ---

export type PromotionCodeRedemption = {
  id: number
  channel: string | null
  discount_type: DiscountType
  discount_amount: number | null
  redeemed_at: string
  location_id: number | null
}

export type PromotionCodePayload = {
  id: number
  code: string
  pool_id: number
  status: string
  redemption_count: number
  created_at: string
  redemptions?: PromotionCodeRedemption[]
}

type PaginationPayload = {
  total_pages: number
  per_page: number
  current_page: number
  total_count: number
}

type GetPromotionCodesResponse = {
  promotion_codes: PromotionCodePayload[]
  pagination: PaginationPayload
}

type GetPromotionCodesParams = {
  promotionId: string
  page?: number
  per_page?: number
  status?: string
  query?: string
  created_after?: string
  created_before?: string
}

type GenerateCodesParams = {
  promotionId: string
  count: number
  prefix?: string
  code_length?: number
}

type GenerateCodesResponse = {
  pool_id?: number
  codes_generated?: number
  message?: string
  count?: number
}

type ExportCodesParams = {
  promotionId: string
  status?: string
  created_after?: string
  created_before?: string
}

type ExportCodesResponse = {
  message: string
}

// --- API endpoints ---

export const promotionApi = api.injectEndpoints({
  overrideExisting: false,
  endpoints: build => ({
    getPromotions: build.query<
      GetPromotionsResponse,
      GetPromotionsParams | void
    >({
      query: (params = {}) => {
        const queryParams = new URLSearchParams()
        if (params && params.page) queryParams.set('page', String(params.page))
        if (params && params.per_page)
          queryParams.set('per_page', String(params.per_page))
        const qs = queryParams.toString()
        return { url: qs ? `promotions?${qs}` : 'promotions' }
      },
      transformResponse: (raw: { promotions: RawPromotionPayload[] }) => ({
        promotions: raw.promotions.map(transformPromotion),
      }),
      providesTags: ['Promotion'],
    }),

    getPromotion: build.query<GetPromotionResponse, string>({
      query: id => ({ url: `promotions/${id}` }),
      transformResponse: (raw: { promotion: RawPromotionPayload }) => ({
        promotion: transformPromotion(raw.promotion),
      }),
      providesTags: (_result, _error, id) => [{ type: 'Promotion', id }],
    }),

    createPromotion: build.mutation<
      GetPromotionResponse,
      CreatePromotionParams
    >({
      query: params => ({
        url: 'promotions',
        method: 'POST',
        body: params,
      }),
      transformResponse: (raw: { promotion: RawPromotionPayload }) => ({
        promotion: transformPromotion(raw.promotion),
      }),
      invalidatesTags: ['Promotion'],
    }),

    updatePromotion: build.mutation<
      GetPromotionResponse,
      UpdatePromotionParams
    >({
      query: ({ id, ...body }) => ({
        url: `promotions/${id}`,
        method: 'PATCH',
        body,
      }),
      transformResponse: (raw: { promotion: RawPromotionPayload }) => ({
        promotion: transformPromotion(raw.promotion),
      }),
      invalidatesTags: ['Promotion'],
    }),

    deactivatePromotion: build.mutation<GetPromotionResponse, string>({
      query: id => ({
        url: `promotions/${id}`,
        method: 'DELETE',
      }),
      transformResponse: (raw: { promotion: RawPromotionPayload }) => ({
        promotion: transformPromotion(raw.promotion),
      }),
      invalidatesTags: ['Promotion'],
    }),

    reactivatePromotion: build.mutation<GetPromotionResponse, string>({
      query: id => ({
        url: `promotions/${id}/reactivate`,
        method: 'POST',
      }),
      transformResponse: (raw: { promotion: RawPromotionPayload }) => ({
        promotion: transformPromotion(raw.promotion),
      }),
      invalidatesTags: ['Promotion'],
    }),

    getPromotionCodes: build.query<
      GetPromotionCodesResponse,
      GetPromotionCodesParams
    >({
      query: ({ promotionId, ...params }) => {
        const queryParams = new URLSearchParams()
        if (params.page) queryParams.set('page', String(params.page))
        if (params.per_page)
          queryParams.set('per_page', String(params.per_page))
        if (params.status) queryParams.set('status', params.status)
        if (params.query) queryParams.set('query', params.query)
        if (params.created_after)
          queryParams.set('created_after', params.created_after)
        if (params.created_before)
          queryParams.set('created_before', params.created_before)
        const qs = queryParams.toString()
        return {
          url: qs
            ? `promotions/${promotionId}/codes?${qs}`
            : `promotions/${promotionId}/codes`,
        }
      },
      providesTags: ['PromotionCode'],
    }),

    generatePromotionCodes: build.mutation<
      GenerateCodesResponse,
      GenerateCodesParams
    >({
      query: ({ promotionId, ...body }) => ({
        url: `promotions/${promotionId}/codes/generate`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['PromotionCode'],
    }),

    exportPromotionCodes: build.mutation<
      ExportCodesResponse,
      ExportCodesParams
    >({
      query: ({ promotionId, ...body }) => ({
        url: `promotions/${promotionId}/codes/export`,
        method: 'POST',
        body,
      }),
    }),
  }),
})

export const {
  useGetPromotionsQuery,
  useGetPromotionQuery,
  useCreatePromotionMutation,
  useUpdatePromotionMutation,
  useDeactivatePromotionMutation,
  useReactivatePromotionMutation,
  useGetPromotionCodesQuery,
  useGeneratePromotionCodesMutation,
  useExportPromotionCodesMutation,
} = promotionApi
